import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NAVY, GOLD, PAYMENT_MODES } from '../utils/constants';
import { AlertTriangle, ChevronDown, ChevronRight, History, X } from 'lucide-react';
import { methodLabel } from './EventPayments';

// ─── Date helpers ─────────────────────────────────────────────────────────────
const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const toDisplayDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};
const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const getMondayOf = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysBetween = (from, to) =>
  Math.round((new Date(to + 'T00:00:00') - new Date(from + 'T00:00:00')) / 86400000);
// IST calendar date of a timestamptz (created_at is UTC; +05:30 then read as UTC).
const istDateOf = (ts) => new Date(new Date(ts).getTime() + 19800000).toISOString().slice(0, 10);
// bookings.date is DD-MM-YYYY text; ISO form makes it comparable to collected_on.
const cruiseDateISO = (t) => {
  if (!t || !/^\d{2}-\d{2}-\d{4}$/.test(t)) return null;
  const [d, m, y] = t.split('-');
  return `${y}-${m}-${d}`;
};

const buildPresets = () => {
  const today = todayInput();
  const thisMonday = getMondayOf(today);
  const [ty, tm] = today.split('-');
  const firstOfMonth = `${ty}-${tm}-01`;
  const lastMonthEnd = addDays(firstOfMonth, -1);
  const [lmy, lmm] = lastMonthEnd.split('-');
  return [
    { key: 'today',     label: 'Today',      from: today,               to: today },
    { key: 'yesterday', label: 'Yesterday',  from: addDays(today, -1),  to: addDays(today, -1) },
    { key: 'thisWeek',  label: 'This Week',  from: thisMonday,          to: today },
    { key: 'thisMonth', label: 'This Month', from: firstOfMonth,        to: today },
    { key: 'lastMonth', label: 'Last Month', from: `${lmy}-${lmm}-01`,  to: lastMonthEnd },
    { key: 'custom',    label: 'Custom',     from: null,                to: null },
  ];
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtSigned = (n) => (n < 0 ? `−₹${fmt(-n)}` : `₹${fmt(n)}`);

// ─── Styles (RangeReports conventions) ────────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const sectionLabel = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNum = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
const tfootStyle = { padding: '9px 12px', fontWeight: 700, fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand)', textAlign: 'right', background: 'var(--color-sunken)', borderTop: '1px solid var(--color-border-strong)', fontVariantNumeric: 'tabular-nums' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 10 };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };
const badge = (bg, border, color) => ({ display: 'inline-block', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase', padding: '1px 6px', borderRadius: 4, background: bg, border: `1px solid ${border}`, color, marginLeft: 6, verticalAlign: 'middle' });
const fieldStyle = { padding: '7px 10px', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box', background: '#fff', color: NAVY, colorScheme: 'light' };
const labelStyle = { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 };

// ─── Ledger math ──────────────────────────────────────────────────────────────
// Nets events into per-day summaries and per-booking lines.
//
// Day totals sum EVERY event. Booking lines net ALL events per
// booking x mode x day REGARDLESS of reference — netting runs FIRST, so an
// insert cancelled by later corrections (import_redate churn, undo pairs,
// same-day zero-and-re-record) nets to zero and is hidden. A line's net
// always reflects the booking's complete day activity; nothing is ever
// subtracted from a group before netting.
//
// Cross-booking fold, applied AFTER netting: the Excel import deletes and
// reinserts whole days under NEW booking ids, so the reversal (old id, all
// booking_delete* events, net -X) and the re-entry (new id, all
// booking_insert* events, net +X) are separate groups. Those are paired by
// day x mode x |net| and hidden together. Only reference-PURE groups are
// fold-eligible — a group with mixed references carries real information
// and always shows its net.
//
// Remaining negatives: all-delete groups are labelled "Booking deleted";
// any other non-refund negative is flagged suspect (money cannot
// un-arrive — usually the phantom-minus half of a cross-day correction).
const buildReport = (events) => {
  const dayMap = new Map();
  events.forEach(ev => {
    const day = ev.collected_on;
    if (!dayMap.has(day)) dayMap.set(day, { modeTotals: {}, otherTotal: 0, total: 0, hasBackfill: false, hasAmended: false, groups: new Map() });
    const d = dayMap.get(day);
    const amt = Number(ev.amount) || 0;
    // Recorded AFTER the day it belongs to: this day's figure changed since
    // the day itself (late entry, re-date, deletion, import cleanup).
    // Stage 2 backfill events (kind='backfill') are excluded — they wrote
    // ALL of history after the fact and already carry the ≈ marker.
    const amended = ev.kind !== 'backfill' && ev.created_at && istDateOf(ev.created_at) > day;
    if (PAYMENT_MODES.includes(ev.mode)) d.modeTotals[ev.mode] = (d.modeTotals[ev.mode] || 0) + amt;
    else d.otherTotal += amt;
    d.total += amt;
    if (ev.source === 'backfill') d.hasBackfill = true;
    if (amended) d.hasAmended = true;
    const gk = `${ev.booking_id}|${ev.mode}`;
    if (!d.groups.has(gk)) d.groups.set(gk, { bookingId: ev.booking_id, mode: ev.mode, net: 0, hasRefund: false, hasBackfill: false, hasAmended: false, insertOnly: true, deleteOnly: true });
    const g = d.groups.get(gk);
    g.net += amt;
    if (ev.kind === 'refund') g.hasRefund = true;
    if (ev.source === 'backfill') g.hasBackfill = true;
    if (amended) g.hasAmended = true;
    if (!(ev.reference || '').startsWith('booking_insert')) g.insertOnly = false;
    if (!(ev.reference || '').startsWith('booking_delete')) g.deleteOnly = false;
  });

  return [...dayMap.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([day, d]) => {
      const nonZero = [...d.groups.values()].filter(g => Math.abs(g.net) >= 0.5);
      // Group-level fold: pure-delete vs pure-insert, same mode, opposite net.
      const folded = new Set();
      const byMode = new Map();
      nonZero.forEach(g => {
        if (!byMode.has(g.mode)) byMode.set(g.mode, []);
        byMode.get(g.mode).push(g);
      });
      byMode.forEach(list => {
        const dels = list.filter(g => g.deleteOnly && g.net < 0);
        const inss = list.filter(g => g.insertOnly && g.net > 0);
        dels.forEach(del => {
          const match = inss.find(ins => !folded.has(ins) && Math.abs(ins.net + del.net) < 0.5);
          if (match) { folded.add(del); folded.add(match); }
        });
      });
      const lines = nonZero
        .filter(g => !folded.has(g))
        .map(g => {
          const deletion = g.deleteOnly && g.net < 0;
          return { ...g, deletion, suspect: g.net < -0.5 && !g.hasRefund && !deletion };
        })
        .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));
      return { day, ...d, lines, hasSuspect: lines.some(l => l.suspect) };
    });
};

// Group a day's (already netted + folded) lines by INDIVIDUAL cruise date,
// ascending — past sailings first (balances collected after sailing, the case
// this ledger exists for), then this day's sailing, then advance bookings;
// deleted/unparseable bookings last. Presentational only — netting and folding
// already happened in buildReport. Shared by the on-screen expanded day view,
// the PDF export, and the Excel flags, so the three can never disagree.
const groupLinesByCruiseDate = (lines, bookingInfo, day) => {
  const groups = new Map();
  lines.forEach(l => {
    const iso = cruiseDateISO(bookingInfo.get(l.bookingId)?.date);
    const key = iso || 'other';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(l);
  });
  const keys = [...groups.keys()].sort((a, b) => {
    if (a === 'other') return 1;
    if (b === 'other') return -1;
    return a < b ? -1 : 1;
  });
  return keys.map(key => {
    const glines = groups.get(key);
    const subtotal = glines.reduce((s, l) => s + l.net, 0);
    // Mode split of this group's subtotal (known modes first, anything else
    // after; zero nets dropped). Returned as entries so each consumer applies
    // its own currency formatting (₹ on screen, Rs. in the PDF).
    const modeNets = new Map();
    glines.forEach(l => modeNets.set(l.mode, (modeNets.get(l.mode) || 0) + l.net));
    const modeSplit = [...PAYMENT_MODES, ...[...modeNets.keys()].filter(m => !PAYMENT_MODES.includes(m))]
      .filter(m => Math.abs(modeNets.get(m) || 0) >= 0.5)
      .map(m => [m, modeNets.get(m)]);
    const isPast = key !== 'other' && key < day;
    const rel = key === 'other' ? null
      : isPast ? 'Balance after sailing'
      : key === day ? 'This day' : 'Advance';
    const label = key === 'other' ? 'Deleted or unknown bookings' : `Cruise ${toDisplayDate(key)}`;
    return { key, label, rel, isPast, lines: glines, subtotal, modeSplit };
  });
};

// ─── Booking ledger drill-down (raw events + re-date) ─────────────────────────
function BookingLedgerModal({ bookingId, bookingName, onClose, onChanged }) {
  const [events, setEvents] = useState(null);
  const [form, setForm] = useState(null); // { mode, amount, from, to, note }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchEvents = useCallback(async () => {
    const { data } = await supabase
      .from('booking_collections')
      .select('id, collected_on, mode, amount, kind, reference, source, created_at')
      .eq('booking_id', bookingId)
      .order('collected_on')
      .order('created_at');
    setEvents(data || []);
  }, [bookingId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleRedate = async () => {
    if (!form) return;
    const amt = Number(form.amount);
    if (!amt || amt <= 0) { setError('Enter a valid amount.'); return; }
    if (!form.to || form.to === form.from) { setError('Choose a different to-date.'); return; }
    if (form.to > todayInput()) { setError('To-date cannot be in the future.'); return; }
    setSaving(true);
    setError('');
    const { error: err } = await supabase.rpc('redate_booking_payment', {
      p_booking_id: bookingId,
      p_mode: form.mode,
      p_amount: amt,
      p_from_date: form.from,
      p_to_date: form.to,
      p_note: form.note?.trim() || null,
    });
    setSaving(false);
    if (err) { setError('Re-date failed: ' + err.message); return; }
    setForm(null);
    fetchEvents();
    onChanged();
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto', padding: '22px 24px 20px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: NAVY }}>Ledger — {bookingName}</h3>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-ink-muted)', padding: 2 }}>
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: '0.76rem', color: 'var(--color-ink-muted)' }}>
          Every raw event, including churn that the report nets away. Click a positive event to move it to a different date.
        </p>

        {events === null ? (
          <div style={{ padding: '24px 0', fontSize: '0.85rem', color: 'var(--color-ink-muted)' }}>Loading…</div>
        ) : (
          <div style={{ ...tableWrap, marginBottom: 16 }}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Date</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Mode</th>
                  <th style={thStyle}>Amount</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Kind</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Source</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Reference</th>
                  <th style={{ ...thStyle, textAlign: 'left' }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr key={ev.id}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{toDisplayDate(ev.collected_on)}</td>
                    <td style={tdStyle}>{ev.mode}</td>
                    <td style={{ ...tdNum, color: Number(ev.amount) < 0 ? '#dc2626' : 'var(--color-ink-brand)' }}>{fmtSigned(Number(ev.amount))}</td>
                    <td style={tdStyle}>{ev.kind}</td>
                    <td style={tdStyle}>{ev.source}</td>
                    <td style={{ ...tdStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.reference || ''}</td>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                      {Number(ev.amount) > 0 && (
                        <button
                          onClick={() => { setError(''); setForm({ mode: ev.mode, amount: String(ev.amount), from: ev.collected_on, to: '', note: '' }); }}
                          style={{ fontSize: '0.68rem', fontWeight: 700, color: NAVY, background: 'var(--color-sunken)', border: '1px solid var(--color-border-strong)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
                        >
                          Re-date
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: 'var(--color-ink-faint)' }}>No ledger events for this booking.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {form && (
          <div style={{ border: '1px solid var(--color-border-strong)', borderRadius: 10, padding: '14px 16px', background: 'var(--color-sunken)' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-ink-muted)', marginBottom: 10 }}>
              Re-date {form.mode} payment
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
              <div>
                <label style={labelStyle}>Amount (₹)</label>
                <input type="number" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} style={{ ...fieldStyle, width: 110 }} />
              </div>
              <div>
                <label style={labelStyle}>From</label>
                <input type="date" value={form.from} onChange={e => setForm(f => ({ ...f, from: e.target.value }))} style={fieldStyle} />
              </div>
              <div>
                <label style={labelStyle}>To</label>
                <input type="date" value={form.to} max={todayInput()} onChange={e => setForm(f => ({ ...f, to: e.target.value }))} style={fieldStyle} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <label style={labelStyle}>Note (optional)</label>
                <input type="text" value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={{ ...fieldStyle, width: '100%' }} />
              </div>
            </div>
            <p style={{ margin: '0 0 10px', fontSize: '0.72rem', color: 'var(--color-ink-muted)', lineHeight: 1.4 }}>
              Writes a reversal pair in the ledger only — the booking's payment columns are untouched, so totals stay reconciled.
            </p>
            {error && <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '0 0 10px' }}>{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleRedate} disabled={saving}
                style={{ background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: '0.8rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving…' : 'Move Payment'}
              </button>
              <button
                onClick={() => { setForm(null); setError(''); }}
                style={{ background: 'none', color: 'var(--color-ink-muted)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '8px 14px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function CollectionsReport({ onNavigate }) {
  const PRESETS = buildPresets();
  const [activePreset, setActivePreset] = useState('today');
  const [fromDate, setFromDate] = useState(todayInput());
  const [toDate, setToDate] = useState(todayInput());
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(null);
  const [bookingInfo, setBookingInfo] = useState(new Map());
  const [hasOther, setHasOther] = useState(false);
  const [credits, setCredits] = useState([]);
  const [eventPays, setEventPays] = useState([]); // event_payments in range, by payment_date
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [drillTarget, setDrillTarget] = useState(null); // { bookingId, name }

  const rangeExceeds31 = fromDate && toDate && daysBetween(fromDate, toDate) > 30;
  const rangeValid = fromDate && toDate && fromDate <= toDate && !rangeExceeds31;

  const fetchReport = useCallback(async (from, to) => {
    setLoading(true);
    const { data: events } = await supabase
      .from('booking_collections')
      .select('id, booking_id, collected_on, mode, amount, kind, reference, source, created_at')
      .gte('collected_on', from)
      .lte('collected_on', to)
      .limit(5000);
    const evts = events || [];

    const ids = [...new Set(evts.map(e => e.booking_id))];
    const info = new Map();
    const CHUNK = 100;
    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
    const results = await Promise.all(
      chunks.map(chunk =>
        supabase.from('bookings').select('id, booking_name, partner_name, date, cruise_type').in('id', chunk)
      )
    );
    results.flatMap(r => r.data || []).forEach(b => info.set(b.id, b));

    setBookingInfo(info);
    setDays(buildReport(evts));
    setHasOther(evts.some(e => !PAYMENT_MODES.includes(e.mode)));

    // Unallocated partner credit received in the range: real money on a
    // real date that is deliberately NOT in booking_collections until it
    // is allocated to bookings (then it lands in the table above, on this
    // same paid_on date, and the day's total money received is unchanged).
    const { data: pays } = await supabase
      .from('partner_payments')
      .select('id, partner_id, amount, mode, paid_on')
      .gte('paid_on', from)
      .lte('paid_on', to)
      .limit(2000);
    let creditRows = [];
    if (pays && pays.length) {
      const [allocRes, nameRes] = await Promise.all([
        supabase.from('partner_payment_allocations')
          .select('payment_id, amount')
          .in('payment_id', pays.map(p => p.id))
          .limit(5000),
        supabase.from('partners')
          .select('id, name')
          .in('id', [...new Set(pays.map(p => p.partner_id))]),
      ]);
      const allocBy = new Map();
      (allocRes.data || []).forEach(a =>
        allocBy.set(a.payment_id, (allocBy.get(a.payment_id) || 0) + Number(a.amount)));
      const nameBy = new Map((nameRes.data || []).map(p => [p.id, p.name]));
      creditRows = pays
        .map(p => ({
          ...p,
          partner_name: nameBy.get(p.partner_id) || 'Unknown partner',
          unallocated: Number(p.amount) - (allocBy.get(p.id) || 0),
        }))
        .filter(c => c.unallocated > 0.5)
        .sort((a, b) => (a.paid_on < b.paid_on ? -1 : 1));
    }
    setCredits(creditRows);

    // Event payments in the range (deposits, balances and refunds on weddings,
    // charters, corporate bookings): real money on the day it moved, on this
    // report's payment-date basis. A separate labelled stream exactly like the
    // partner credit above: an event is not a booking and has no sailing to
    // reconcile against, so it never enters the day-by-mode figures, Net
    // Collection or Cash in Hand (GAPS 3b-ii: one date basis per figure).
    const { data: evPays } = await supabase
      .from('event_payments')
      .select('id, event_id, amount, payment_date, method, reference, note')
      .gte('payment_date', from)
      .lte('payment_date', to)
      .limit(2000);
    let eventRows = [];
    if (evPays && evPays.length) {
      const { data: evs } = await supabase
        .from('events')
        .select('id, title, event_date')
        .in('id', [...new Set(evPays.map(p => p.event_id))]);
      const evBy = new Map((evs || []).map(e => [e.id, e]));
      eventRows = evPays
        .map(p => ({ ...p, amount: Number(p.amount), event: evBy.get(p.event_id) || null }))
        .sort((a, b) => (a.payment_date < b.payment_date ? -1 : a.payment_date > b.payment_date ? 1 : 0));
    }
    setEventPays(eventRows);
    setExpandedDays(new Set());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (rangeValid) fetchReport(fromDate, toDate);
  }, [fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPreset = (p) => {
    setActivePreset(p.key);
    if (p.from) { setFromDate(p.from); setToDate(p.to); }
  };

  const toggleDay = (day) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  const modeCols = hasOther ? [...PAYMENT_MODES, 'Other'] : PAYMENT_MODES;
  const rangeTotals = (days || []).reduce((acc, d) => {
    PAYMENT_MODES.forEach(m => { acc[m] = (acc[m] || 0) + (d.modeTotals[m] || 0); });
    acc.Other = (acc.Other || 0) + d.otherTotal;
    acc.total = (acc.total || 0) + d.total;
    return acc;
  }, {});
  const anyBackfill = (days || []).some(d => d.hasBackfill);
  const creditByDay = new Map();
  credits.forEach(c => creditByDay.set(c.paid_on, (creditByDay.get(c.paid_on) || 0) + c.unallocated));
  const creditTotal = credits.reduce((s, c) => s + c.unallocated, 0);
  const eventByDay = new Map();
  eventPays.forEach(p => eventByDay.set(p.payment_date, (eventByDay.get(p.payment_date) || 0) + p.amount));
  const eventTotal = eventPays.reduce((s, p) => s + p.amount, 0);

  const renderDayLine = (l) => {
    const info = bookingInfo.get(l.bookingId);
    const name = info?.booking_name || 'Deleted booking';
    return (
      <div
        key={`${l.bookingId}|${l.mode}`}
        onClick={(e) => { e.stopPropagation(); setDrillTarget({ bookingId: l.bookingId, name }); }}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem' }}
        onMouseEnter={e => { e.currentTarget.style.background = '#ffffff'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ color: 'var(--color-ink-brand-softer)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
          {info?.date && <span style={{ color: 'var(--color-ink-faint)' }}> · cruise {info.date}</span>}
          <span style={{ color: 'var(--color-ink-faint)' }}> · {l.mode}</span>
          {l.hasRefund && l.net < 0 && <span style={badge('#fef2f2', '#fca5a5', '#991b1b')}>Refund</span>}
          {l.hasBackfill && <span style={badge('var(--color-sunken)', 'var(--color-border-strong)', 'var(--color-ink-muted)')}>Backfill ≈</span>}
          {l.hasAmended && <span style={badge('#eff6ff', '#93c5fd', '#1d4ed8')}>Amended later</span>}
          {l.deletion && <span style={badge('#fef2f2', '#fca5a5', '#991b1b')}>Booking deleted</span>}
          {l.suspect && <span style={badge('#fffbeb', '#fcd34d', '#b45309')}>Suspect negative</span>}
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 12, color: l.net < 0 ? '#dc2626' : 'var(--color-ink-brand)' }}>
          {fmtSigned(l.net)}
        </span>
      </div>
    );
  };

  const presetBtnStyle = (active) => ({
    padding: '8px 14px',
    background: active ? NAVY : '#fff',
    color: active ? '#fff' : 'var(--color-ink-secondary)',
    border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
    borderRadius: 8,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.12s, color 0.12s',
  });

  // ─── Exports ─────────────────────────────────────────────────────────────────
  // Both exports read the SAME state the screen renders (`days` from
  // buildReport, `bookingInfo`, `credits`) and the shared
  // groupLinesByCruiseDate helper — no re-fetch, no re-computation — so the
  // exported figures cannot drift from what is displayed.
  const fromDisp = toDisplayDate(fromDate);
  const toDisp = toDisplayDate(toDate);
  const rangeLabel = fromDate === toDate ? fromDisp : `${fromDisp} to ${toDisp}`;
  const fileStem = fromDate === toDate ? `Collections_${fromDisp}` : `Collections_${fromDisp}_to_${toDisp}`;

  // Excel: one flat row per (already netted + folded) booking line, oldest day
  // first, for pivoting and reconciliation. Amounts are raw numbers.
  const exportExcel = async () => {
    if (!days) return;
    try {
      const { default: ExcelJS } = await import('exceljs');
      const wb = new ExcelJS.Workbook();
      const reportTitle = `Alfresco Grand — Collections — ${rangeLabel}`;

      const NAVY_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0A1628' } };
      const ALT_FILL  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F5F1' } };
      const THIN = { style: 'thin', color: { argb: 'FFD0C8B8' } };
      const BORDER = { top: THIN, left: THIN, bottom: THIN, right: THIN };

      // RangeReports' addSheet pattern; numCols (1-based) get number format +
      // right alignment so amounts stay numeric and pivotable.
      const addSheet = (name, colHeaders, dataRows, numCols = []) => {
        const ws = wb.addWorksheet(name);
        const r1 = ws.addRow([reportTitle]);
        r1.height = 24;
        r1.getCell(1).font = { bold: true, size: 11, color: { argb: 'FF0A1628' } };
        const r2 = ws.addRow([]);
        r2.height = 8;
        const r3 = ws.addRow(colHeaders);
        r3.height = 18;
        for (let c = 1; c <= colHeaders.length; c++) {
          const cell = r3.getCell(c);
          cell.fill = NAVY_FILL;
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 };
          cell.border = BORDER;
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        }
        dataRows.forEach((values, idx) => {
          const row = ws.addRow(values);
          row.height = 15;
          const useFill = idx % 2 === 1;
          for (let c = 1; c <= colHeaders.length; c++) {
            const cell = row.getCell(c);
            cell.border = BORDER;
            cell.alignment = { vertical: 'middle', horizontal: numCols.includes(c) ? 'right' : 'left' };
            if (numCols.includes(c)) cell.numFmt = '#,##0';
            if (useFill) cell.fill = ALT_FILL;
          }
        });
        for (let c = 1; c <= colHeaders.length; c++) {
          let maxW = String(colHeaders[c - 1]).length;
          dataRows.forEach(row => { const len = String(row[c - 1] ?? '').length; if (len > maxW) maxW = len; });
          ws.getColumn(c).width = Math.min(maxW + 3, 55);
        }
        ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 3, topLeftCell: 'A4' }];
        return ws;
      };

      const detailRows = [];
      [...days].sort((a, b) => (a.day < b.day ? -1 : 1)).forEach(d => {
        groupLinesByCruiseDate(d.lines, bookingInfo, d.day).forEach(g => {
          g.lines.forEach(l => {
            const info = bookingInfo.get(l.bookingId);
            const note = l.hasRefund && l.net < 0 ? 'Refund'
              : l.deletion ? 'Booking deleted'
              : l.suspect ? 'Suspect negative' : '';
            detailRows.push([
              toDisplayDate(d.day),
              info?.date || '',
              info?.booking_name || 'Deleted booking',
              info?.partner_name || '',
              l.mode,
              Math.round(l.net),
              l.hasBackfill ? 'Yes' : '',
              l.hasAmended ? 'Yes' : '',
              g.isPast ? 'Yes' : '',
              note,
            ]);
          });
        });
      });
      addSheet('Collections Detail',
        ['Collection Date', 'Cruise Date', 'Booking Name', 'Partner', 'Mode', 'Net Amount (Rs)', 'Approximate (backfill)', 'Amended Later', 'Balance After Sailing', 'Note'],
        detailRows.length ? detailRows : [['No collections in this period', '', '', '', '', '', '', '', '', '']],
        [6]
      );

      const hasCredit = credits.length > 0;
      addSheet('Unallocated Credit',
        hasCredit ? ['Paid On', 'Partner', 'Mode', 'Payment (Rs)', 'Still Unallocated (Rs)'] : ['Note'],
        hasCredit
          ? [
              ...credits.map(c => [toDisplayDate(c.paid_on), c.partner_name, c.mode, Math.round(Number(c.amount)), Math.round(c.unallocated)]),
              ['TOTAL', '', '', '', Math.round(creditTotal)],
            ]
          : [['No unallocated partner credit received in this period']],
        hasCredit ? [4, 5] : []
      );

      const hasEventPays = eventPays.length > 0;
      addSheet('Event Payments',
        hasEventPays ? ['Paid On', 'Event', 'Event Date', 'Method', 'Reference', 'Note', 'Amount (Rs)'] : ['Note'],
        hasEventPays
          ? [
              ...eventPays.map(p => [toDisplayDate(p.payment_date), p.event?.title || 'Unknown event', p.event?.event_date ? toDisplayDate(p.event.event_date) : '', methodLabel(p.method), p.reference || '', p.note || '', Math.round(p.amount)]),
              ['TOTAL', '', '', '', '', '', Math.round(eventTotal)],
            ]
          : [['No event payments in this period']],
        hasEventPays ? [7] : []
      );

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileStem}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  };

  // PDF: the on-screen summary — day totals by mode, the cruise-date groupings
  // with their subtotals, and the unallocated credit — on A4 portrait.
  // Helvetica has no ₹ or ≈, so amounts use "Rs." and the backfill/amended
  // markers become ~ and * with a legend.
  const exportPDF = () => {
    if (!days) return;
    const pdfSigned = (n) => (n < 0 ? `-Rs.${fmt(-n)}` : `Rs.${fmt(n)}`);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    doc.setFontSize(14);
    doc.setTextColor(10, 22, 40);
    doc.text('Alfresco Grand - Collections Report', 10, 14);
    doc.setFontSize(8);
    doc.setTextColor(100, 90, 70);
    doc.text(`Period: ${rangeLabel}`, 10, 21);

    const ML = 10, MR = 10;
    const HEAD  = { fillColor: [10, 22, 40], textColor: 255, fontStyle: 'bold', fontSize: 7 };
    const BODY  = { fontSize: 7 };
    const STYLES = { overflow: 'linebreak', cellPadding: 1.5 };
    const ALT   = { fillColor: [249, 247, 243] };
    const MARGIN = { left: ML, right: MR };
    let y = 27;
    const gap = 5;
    const addSection = (title) => {
      if (y > 270) { doc.addPage(); y = 12; }
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(122, 106, 80);
      doc.text(title.toUpperCase(), ML, y);
      doc.setFont('helvetica', 'normal');
      y += 3;
    };

    addSection('Daily Collections by Mode');
    const modeW = Math.floor((190 - 26 - 26) / modeCols.length);
    autoTable(doc, {
      startY: y,
      head: [['Date', ...modeCols, 'Total']],
      body: [
        ...days.map(d => [
          `${toDisplayDate(d.day)}${d.hasBackfill ? ' ~' : ''}${d.hasAmended ? ' *' : ''}`,
          ...modeCols.map(m => { const v = m === 'Other' ? d.otherTotal : (d.modeTotals[m] || 0); return v ? pdfSigned(v) : ''; }),
          pdfSigned(d.total),
        ]),
        ['TOTAL',
          ...modeCols.map(m => { const v = m === 'Other' ? (rangeTotals.Other || 0) : (rangeTotals[m] || 0); return v ? pdfSigned(v) : ''; }),
          pdfSigned(rangeTotals.total || 0)],
      ],
      headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
      columnStyles: {
        0: { cellWidth: 26, halign: 'left' },
        ...Object.fromEntries(modeCols.map((_, i) => [i + 1, { cellWidth: modeW, halign: 'right' }])),
        [modeCols.length + 1]: { cellWidth: 26, halign: 'right', fontStyle: 'bold' },
      },
      alternateRowStyles: ALT, margin: MARGIN,
    });
    y = doc.lastAutoTable.finalY + 3;
    const anyAmended = days.some(d => d.hasAmended);
    if (anyBackfill || anyAmended) {
      doc.setFontSize(6.5);
      doc.setTextColor(122, 106, 80);
      const legend = [
        anyBackfill ? '~ includes backfilled history (event dates approximate)' : null,
        anyAmended ? '* amended after the day (late entry, re-date, deletion, or import cleanup)' : null,
      ].filter(Boolean).join('   ');
      doc.text(legend, ML, y + 2);
      y += 5;
    }
    y += gap - 3;

    const groupRows = [];
    days.forEach(d => {
      groupLinesByCruiseDate(d.lines, bookingInfo, d.day).forEach(g => {
        groupRows.push([
          toDisplayDate(d.day),
          g.key === 'other' ? 'Deleted / unknown' : toDisplayDate(g.key),
          g.rel || '',
          g.modeSplit.map(([m, n]) => `${m} ${pdfSigned(n)}`).join(', '),
          pdfSigned(g.subtotal),
        ]);
      });
    });
    if (groupRows.length) {
      addSection('Collections by Cruise Date');
      autoTable(doc, {
        startY: y,
        head: [['Collected On', 'Cruise Date', 'Timing', 'Mode Split', 'Subtotal']],
        body: groupRows,
        headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
        columnStyles: {
          0: { cellWidth: 26, halign: 'left' },
          1: { cellWidth: 30, halign: 'left' },
          2: { cellWidth: 32, halign: 'left' },
          3: { cellWidth: 72, halign: 'left' },
          4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: ALT, margin: MARGIN,
      });
      y = doc.lastAutoTable.finalY + gap;
    }

    if (credits.length > 0) {
      addSection('Unallocated Partner Credit');
      autoTable(doc, {
        startY: y,
        head: [['Paid On', 'Partner', 'Mode', 'Payment', 'Still Unallocated']],
        body: [
          ...credits.map(c => [toDisplayDate(c.paid_on), c.partner_name, c.mode, pdfSigned(Number(c.amount)), pdfSigned(c.unallocated)]),
          ['TOTAL', '', '', '', pdfSigned(creditTotal)],
        ],
        headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
        columnStyles: {
          0: { cellWidth: 26, halign: 'left' },
          1: { cellWidth: 74, halign: 'left' },
          2: { cellWidth: 30, halign: 'left' },
          3: { cellWidth: 30, halign: 'right' },
          4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: ALT, margin: MARGIN,
      });
      y = doc.lastAutoTable.finalY + 3;
      doc.setFontSize(6.5);
      doc.setTextColor(122, 106, 80);
      doc.text('Money received from partners but not yet applied to bookings - outside the day-by-mode figures above until allocated.', ML, y + 2);
    }

    if (eventPays.length > 0) {
      y += 7;
      addSection('Event Payments');
      autoTable(doc, {
        startY: y,
        head: [['Paid On', 'Event', 'Method', 'Reference', 'Amount']],
        body: [
          ...eventPays.map(p => [toDisplayDate(p.payment_date), `${p.event?.title || 'Unknown event'}${p.event?.event_date ? ` (${toDisplayDate(p.event.event_date)})` : ''}`, methodLabel(p.method), [p.reference, p.note].filter(Boolean).join(' - '), pdfSigned(p.amount)]),
          ['TOTAL', '', '', '', pdfSigned(eventTotal)],
        ],
        headStyles: HEAD, bodyStyles: BODY, styles: STYLES,
        columnStyles: {
          0: { cellWidth: 26, halign: 'left' },
          1: { cellWidth: 64, halign: 'left' },
          2: { cellWidth: 26, halign: 'left' },
          3: { cellWidth: 44, halign: 'left' },
          4: { cellWidth: 30, halign: 'right', fontStyle: 'bold' },
        },
        alternateRowStyles: ALT, margin: MARGIN,
      });
      y = doc.lastAutoTable.finalY + 3;
      doc.setFontSize(6.5);
      doc.setTextColor(122, 106, 80);
      doc.text('Money moved on events (weddings, charters, corporate) on the day it moved - outside the day-by-mode figures, Net Collection and Cash in Hand.', ML, y + 2);
    }

    doc.save(`${fileStem}.pdf`);
  };

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Collections</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
        Money actually collected per day and mode, from the collections ledger. Figures are net of
        same-day churn; refunds count negative on the refund date.
      </p>

      {/* Period */}
      <div style={card}>
        <div style={sectionLabel}>Period</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: activePreset === 'custom' ? 16 : 0 }}>
          {PRESETS.map(p => (
            <button key={p.key} style={presetBtnStyle(activePreset === p.key)} onClick={() => selectPreset(p)}>
              {p.label}
            </button>
          ))}
          <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--color-border)', margin: '0 4px' }} aria-hidden="true" />
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>Day</label>
          <input
            type="date"
            value={activePreset === 'day' ? fromDate : ''}
            onChange={e => {
              if (!e.target.value) return;
              setActivePreset('day');
              setFromDate(e.target.value);
              setToDate(e.target.value);
            }}
            aria-label="Jump to a specific day"
            style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '7px 10px', color: NAVY, fontSize: '0.85rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }}
          />
        </div>
        {activePreset === 'custom' && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>From</span>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>To</span>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 12px', color: NAVY, fontSize: '0.9rem', fontWeight: 600, outline: 'none', colorScheme: 'light' }} />
            {fromDate && toDate && fromDate > toDate && (
              <span style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>From date must be before To date.</span>
            )}
            {rangeExceeds31 && fromDate <= toDate && (
              <span style={{ fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>Maximum range is 31 days.</span>
            )}
          </div>
        )}
      </div>

      {/* Export buttons — RangeReports conventions */}
      {days !== null && !loading && (days.length > 0 || credits.length > 0) && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
          <button onClick={exportExcel}
            style={{ padding: '9px 20px', background: '#166534', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
            Export Excel (.xlsx)
          </button>
          <button onClick={exportPDF}
            style={{ padding: '9px 20px', background: '#991b1b', color: '#fff', border: 'none', borderRadius: 9, fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>
            Export PDF
          </button>
        </div>
      )}

      {/* Day x mode summary */}
      <div style={card}>
        <div style={sectionLabel}>Daily Collections by Mode</div>
        {loading || days === null ? (
          <div style={{ padding: '24px 0', fontSize: '0.85rem', color: 'var(--color-ink-muted)' }}>Loading…</div>
        ) : days.length === 0 ? (
          <div style={{ padding: '24px 0', fontSize: '0.85rem', color: 'var(--color-ink-faint)' }}>No ledger events in this period.</div>
        ) : (
          <div style={tableWrap}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Date</th>
                  {modeCols.map(m => (
                    <th key={m} style={thStyle}>
                      {m}
                      {m === 'Razorpay' && (
                        <div style={{ fontSize: '0.55rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0, color: 'var(--color-ink-faint)' }}>
                          gross — settlement not tracked
                        </div>
                      )}
                    </th>
                  ))}
                  <th style={thStyle}>Total</th>
                </tr>
              </thead>
              <tbody>
                {days.map(d => {
                  const expanded = expandedDays.has(d.day);
                  return [
                    <tr key={d.day} onClick={() => toggleDay(d.day)} style={{ cursor: 'pointer' }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontWeight: 700, color: NAVY }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {expanded
                            ? <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
                            : <ChevronRight size={14} strokeWidth={2} aria-hidden="true" />}
                          {toDisplayDate(d.day)}
                          {d.hasBackfill && <span title="Includes backfilled events — dates approximate" style={{ color: 'var(--color-ink-faint)', fontWeight: 600 }}>≈</span>}
                          {d.hasAmended && (
                            <span title="Amended after the day — includes events recorded later (late entry, re-date, deletion, or import cleanup), so this figure has changed since the day itself">
                              <History size={13} strokeWidth={2} color="#1d4ed8" aria-label="Amended after the day" />
                            </span>
                          )}
                          {d.hasSuspect && (
                            <span title="A booking nets negative this day without a refund — possible cross-day correction">
                              <AlertTriangle size={13} strokeWidth={2} color="#b45309" aria-label="Suspect negative" />
                            </span>
                          )}
                        </span>
                      </td>
                      {modeCols.map(m => {
                        const v = m === 'Other' ? d.otherTotal : (d.modeTotals[m] || 0);
                        return (
                          <td key={m} style={{ ...tdNum, color: v < 0 ? '#dc2626' : v ? 'var(--color-ink-brand)' : 'var(--color-ink-faint)' }}>
                            {v ? fmtSigned(v) : ''}
                          </td>
                        );
                      })}
                      <td style={{ ...tdNum, fontWeight: 700, color: d.total < 0 ? '#dc2626' : NAVY }}>
                        {fmtSigned(d.total)}
                        {creditByDay.has(d.day) && (
                          <span title="Unallocated partner credit also received this day (see the card below) — not in this figure until allocated"
                                style={{ marginLeft: 6, fontSize: '0.66rem', fontWeight: 700, color: '#15803d' }}>
                            +{fmtSigned(creditByDay.get(d.day))} cr
                          </span>
                        )}
                        {eventByDay.has(d.day) && (
                          <span title="Event payments also moved this day (see the Event Payments card below) — never in this figure"
                                style={{ marginLeft: 6, fontSize: '0.66rem', fontWeight: 700, color: eventByDay.get(d.day) < 0 ? '#dc2626' : '#1d4ed8' }}>
                            {eventByDay.get(d.day) < 0 ? '' : '+'}{fmtSigned(eventByDay.get(d.day))} events
                          </span>
                        )}
                      </td>
                    </tr>,
                    expanded && (
                      <tr key={`${d.day}-detail`}>
                        <td colSpan={modeCols.length + 2} style={{ padding: 0, borderBottom: '1px solid var(--color-border-subtle)', background: 'var(--color-sunken)' }}>
                          <div style={{ padding: '8px 12px 10px 34px' }}>
                            {d.lines.length === 0 ? (
                              <div style={{ fontSize: '0.78rem', color: 'var(--color-ink-faint)', padding: '4px 0' }}>
                                All movements this day net to zero (data-entry churn or import re-entry).
                              </div>
                            ) : groupLinesByCruiseDate(d.lines, bookingInfo, d.day).map(g => {
                              const color = g.key === 'other' ? 'var(--color-ink-muted)'
                                : g.isPast ? '#b45309'
                                : g.key === d.day ? NAVY : 'var(--color-ink-muted)';
                              const modeSplitStr = g.modeSplit.map(([m, n]) => `${m} ${fmtSigned(n)}`).join(' · ');
                              return (
                                <div key={g.key} style={{ marginBottom: 6 }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0 10px', padding: '5px 8px 2px', borderBottom: '1px solid var(--color-border-subtle)', marginBottom: 2 }}>
                                    <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color }}>
                                      {g.label}
                                      {g.rel && <span style={{ fontWeight: 600, marginLeft: 6, opacity: g.isPast ? 1 : 0.7 }}>· {g.rel}</span>}
                                    </span>
                                    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                                      {modeSplitStr && (
                                        <span style={{ fontSize: '0.66rem', fontWeight: 600, color: 'var(--color-ink-muted)', fontVariantNumeric: 'tabular-nums' }}>{modeSplitStr}</span>
                                      )}
                                      <span style={{ fontSize: '0.74rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: g.subtotal < 0 ? '#dc2626' : color }}>{fmtSigned(g.subtotal)}</span>
                                    </span>
                                  </div>
                                  {g.lines.map(l => renderDayLine(l))}
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tfootStyle, textAlign: 'left' }}>Total</td>
                  {modeCols.map(m => {
                    const v = m === 'Other' ? (rangeTotals.Other || 0) : (rangeTotals[m] || 0);
                    return <td key={m} style={{ ...tfootStyle, color: v < 0 ? '#dc2626' : 'var(--color-ink-brand)' }}>{v ? fmtSigned(v) : ''}</td>;
                  })}
                  <td style={{ ...tfootStyle, color: (rangeTotals.total || 0) < 0 ? '#dc2626' : NAVY }}>{fmtSigned(rangeTotals.total || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)', lineHeight: 1.6 }}>
          Click a day to see its bookings; click a booking for the raw event audit trail and re-dating.
          The clock marks days amended after the fact — their figure changed since the day itself.
          {anyBackfill && ' ≈ marks days that include backfilled history — those event dates are the booking entry date, not necessarily the collection date.'}
        </div>
      </div>

      {credits.length > 0 && (
        <div style={card}>
          <div style={sectionLabel}>Unallocated Partner Credit</div>
          <div style={tableWrap}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Paid on</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Partner</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Mode</th>
                  <th style={thStyle}>Payment</th>
                  <th style={thStyle}>Still unallocated</th>
                </tr>
              </thead>
              <tbody>
                {credits.map(c => (
                  <tr key={c.id}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{toDisplayDate(c.paid_on)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: NAVY }}>{c.partner_name}</td>
                    <td style={tdStyle}>{c.mode}</td>
                    <td style={tdNum}>{fmtSigned(Number(c.amount))}</td>
                    <td style={{ ...tdNum, fontWeight: 700, color: '#15803d' }}>{fmtSigned(c.unallocated)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tfootStyle, textAlign: 'left' }} colSpan={4}>Total unallocated credit received in this period</td>
                  <td style={{ ...tfootStyle, color: '#15803d' }}>{fmtSigned(creditTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)', lineHeight: 1.6 }}>
            Money received from partners but not yet applied to bookings — deliberately outside
            the day-by-mode figures above. When allocated (via the Partner Ledger) it appears in
            the table on this same paid-on date, so a day's total money received never changes,
            only its split between collections and credit.
          </div>
        </div>
      )}

      {eventPays.length > 0 && (
        <div style={card}>
          <div style={sectionLabel}>Event Payments</div>
          <div style={tableWrap}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Paid on</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Event</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Method</th>
                  <th style={{ ...thStyle, textAlign: 'left' }}>Reference</th>
                  <th style={thStyle}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {eventPays.map(p => (
                  <tr key={p.id}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{toDisplayDate(p.payment_date)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: NAVY }}>
                      {p.event?.title || 'Unknown event'}
                      {p.event?.event_date && <span style={{ color: 'var(--color-ink-faint)', fontWeight: 400 }}> · event {toDisplayDate(p.event.event_date)}</span>}
                    </td>
                    <td style={tdStyle}>{methodLabel(p.method)}</td>
                    <td style={tdStyle}>{[p.reference, p.note].filter(Boolean).join(' · ')}</td>
                    <td style={{ ...tdNum, fontWeight: 700, color: p.amount < 0 ? '#dc2626' : '#1d4ed8' }}>
                      {fmtSigned(p.amount)}
                      {p.amount < 0 && <span style={badge('#fef2f2', '#fca5a5', '#991b1b')}>Refund</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...tfootStyle, textAlign: 'left' }} colSpan={4}>Net event money received in this period</td>
                  <td style={{ ...tfootStyle, color: eventTotal < 0 ? '#dc2626' : '#1d4ed8' }}>{fmtSigned(eventTotal)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-ink-faint)', lineHeight: 1.6 }}>
            Deposits, balances and refunds on events (weddings, charters, corporate), on the day the
            money moved. Deliberately outside the day-by-mode figures above, Net Collection and
            Cash in Hand: an event is not a booking and has no sailing to reconcile against.
          </div>
        </div>
      )}

      {drillTarget && (
        <BookingLedgerModal
          bookingId={drillTarget.bookingId}
          bookingName={drillTarget.name}
          onClose={() => setDrillTarget(null)}
          onChanged={() => { if (rangeValid) fetchReport(fromDate, toDate); }}
        />
      )}
    </div>
  );
}
